import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import { BrandRegistryEntry, DEFAULT_BRAND } from './brands.registry';

/**
 * Estado visual del overlay.
 *
 * `booting`   Pantalla idéntica al splash nativo (fondo blanco + logo
 *             SideDrawer gris chico). Se usa como "transición invisible"
 *             entre el native splash y el overlay web.
 * `branded`   Fondo y logo del brand resuelto. Al entrar acá desde
 *             `booting` se anima un crossfade 400ms.
 * `hidden`    Estado terminal después del fadeOut. El componente
 *             sigue montado pero con `display: none` para no interferir.
 */
type OverlayState = 'booting' | 'branded' | 'hidden';

/**
 * Overlay full-screen que reemplaza visualmente al splash nativo.
 *
 * Se monta en AppComponent, arranca matcheando el look del splash
 * nativo (blanco + logo SideDrawer gris), y cuando se resuelve el
 * brandcode hace un crossfade 400ms al branded final.
 *
 * API pública:
 *  - showBooting() / showBranded(entry) para controlar el estado
 *  - fadeOut() para esconderlo y revelar la app
 */
@Component({
  selector: 'app-branded-splash-overlay',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="branded-splash-overlay"
      [class.branded-splash-overlay--hidden]="state() === 'hidden'"
      [class.branded-splash-overlay--fading-out]="fadingOut()"
      [style.background-color]="backgroundColor()"
    >
      <div class="branded-splash-overlay__logos">
        <img
          class="branded-splash-overlay__logo branded-splash-overlay__logo--boot"
          [class.branded-splash-overlay__logo--visible]="state() === 'booting'"
          src="assets/brands/default/splash-logo.svg"
          alt=""
          aria-hidden="true"
        />
        <img
          class="branded-splash-overlay__logo branded-splash-overlay__logo--brand"
          [class.branded-splash-overlay__logo--visible]="state() === 'branded'"
          [src]="brandedLogoPath()"
          alt=""
          aria-hidden="true"
        />
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        position: fixed;
        inset: 0;
        z-index: 99999;
        pointer-events: none;
      }

      .branded-splash-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 400ms ease, opacity 300ms ease;
        opacity: 1;
      }

      .branded-splash-overlay--hidden {
        display: none;
      }

      .branded-splash-overlay--fading-out {
        opacity: 0;
      }

      .branded-splash-overlay__logos {
        position: relative;
        width: 40vmin;
        max-width: 240px;
        aspect-ratio: 1 / 1;
      }

      .branded-splash-overlay__logo {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        opacity: 0;
        transition: opacity 400ms ease;
      }

      .branded-splash-overlay__logo--visible {
        opacity: 1;
      }

      .branded-splash-overlay__logo--boot {
        filter: grayscale(1) opacity(0.6);
      }
    `,
  ],
})
export class BrandedSplashOverlayComponent {
  private readonly document = inject(DOCUMENT);

  readonly state = signal<OverlayState>('booting');
  readonly fadingOut = signal(false);
  readonly currentEntry = signal<BrandRegistryEntry>(DEFAULT_BRAND);

  readonly backgroundColor = computed(() => {
    if (this.state() === 'booting') {
      return '#FFFFFF';
    }
    return this.currentEntry().backgroundColor;
  });

  readonly brandedLogoPath = computed(
    () => this.currentEntry().splashLogoPath
  );

  showBooting(): void {
    this.state.set('booting');
    this.fadingOut.set(false);
  }

  showBranded(entry: BrandRegistryEntry): void {
    this.currentEntry.set(entry);
    this.state.set('branded');
  }

  fadeOut(afterMs: number = 0): Promise<void> {
    return new Promise(resolve => {
      const start = (): void => {
        this.fadingOut.set(true);
        this.document.defaultView?.setTimeout(() => {
          this.state.set('hidden');
          resolve();
        }, 350);
      };
      if (afterMs > 0) {
        this.document.defaultView?.setTimeout(start, afterMs);
      } else {
        start();
      }
    });
  }
}
